import { FC, useMemo } from 'react';

import { GenInternalLink } from '@/components/generics';

import {
  AddressIcon,
  CreditCardIcon,
  DeliveredIcon,
  PackageIcon,
  ScheduledIcon,
} from '@/components/ui/Icons';

import useLocale from '@/hooks/useLocale';

import { TypePlayGeniusOrder } from '@/lib/api/orderManager/types/TypePlayGeniusOrder';
import { TypeExternalPaymentMethod } from '@/lib/api/productCatalog/types';
import { TypeSubEngineInstallment } from '@/lib/api/subscriptionManager/types';

import { DisplayDateFormat } from '@/utils/dates/constants';
import { formatDateForDisplay } from '@/utils/dates/dateFormatters';

import { CTA_NAMES } from '@/utils/rudderstack';

import { TypeAccountERCPageTranslations } from '@/utils/translations/types/TypeAccountERCPageTranslations';

import { InfoRow } from '../../../../../_components/InfoRow';

import { trackClickEvent } from '../../../../utils';
import { useSearchParams } from 'next/navigation';

interface OrderInsightsProps {
  t: TypeAccountERCPageTranslations;
  orderData: TypePlayGeniusOrder;
  installmentData?: TypeSubEngineInstallment;
  paymentMethodData?: TypeExternalPaymentMethod;
  isNextOrder: boolean;
}

const OrderInsights: FC<OrderInsightsProps> = ({
  t,
  orderData,
  installmentData,
  paymentMethodData,
  isNextOrder,
}) => {
  const locale = useLocale();

  const searchParams = useSearchParams();

  const shippingAddress = searchParams?.get('shipping-address');

  const paymentDate = useMemo(
    () =>
      installmentData?.scheduled_date &&
      formatDateForDisplay(
        installmentData.scheduled_date,
        locale,
        DisplayDateFormat.ShortMonthDayYear,
      ),
    [installmentData, locale],
  );

  const OrderDate = () => (
    <InfoRow
      icon={DeliveredIcon}
      label={t.orderDate}
      value={formatDateForDisplay(
        orderData?.created_at || '',
        locale,
        DisplayDateFormat.ShortMonthDayYear,
      )}
    />
  );

  const OrderNumber = () => (
    <InfoRow icon={PackageIcon} label={t.orderNumber}>
      <GenInternalLink
        href={`${orderData?.status_url}?language=${locale}`}
        cta={orderData?.playgenius_order_id || ''}
      />
    </InfoRow>
  );

  const NextPaymentDate = () => (
    <InfoRow
      icon={ScheduledIcon}
      label={t.nextPaymentDate}
      value={paymentDate}
    />
  );

  const PaymentMethod = () => (
    <InfoRow
      icon={CreditCardIcon}
      label={t.paymentMethod}
      value={paymentMethodData?.localized_label}
      shouldLineBreakValueDisplayInMobile
    >
      {installmentData ? (
        <GenInternalLink
          testId="managePaymentLink"
          href={`/account/early-reader-club/${installmentData?.subscription_id}/manage-payment`}
          cta={t.manage}
          onClick={() => {
            trackClickEvent(CTA_NAMES.MANAGE_PAYMENT_METHOD);
          }}
        />
      ) : null}
    </InfoRow>
  );

  const OrderShippingAddress = () => (
    <InfoRow
      icon={AddressIcon}
      label={t.shippingAddress}
      value={shippingAddress ?? orderData?.shipping_address.line1}
      shouldHideOverflow
    >
      <GenInternalLink
        href={`/${locale}/account/early-reader-club/edit-shipping-address/${orderData?.shipping_address.original_uuid}`}
        cta={t.edit}
        onClick={() => {
          trackClickEvent(CTA_NAMES.EDIT_SHIPPING_ADDRESS);
        }}
      />
    </InfoRow>
  );

  return (
    <div className="grid gap-y-0.5 md:gap-y-1 px-6 xl:px-0 mb-4 md:mb-6">
      {isNextOrder ? (
        <>
          <NextPaymentDate />
          <PaymentMethod />
          <OrderShippingAddress />
        </>
      ) : (
        <>
          <OrderDate />
          <OrderNumber />
        </>
      )}
    </div>
  );
};

export default OrderInsights;
